import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";

export type TtsSpeakingState = "started" | "finished" | "cancelled" | null;
export type TtsEchoState = Exclude<TtsSpeakingState, null> | "error";

type State = {
  speakingState: TtsSpeakingState;
  /** The stop key belongs to speech alone: it is playing or queued. */
  stopKeyArmed: boolean;
  completedReplyIds: string[];
  completedReplyText: Record<string, string>;
  markReplyCompleted: (executionId: string, text?: string) => void;
  echoText: string | null;
  /** When `echoText` began playing; null when this store never saw it start. */
  echoStartedAt: number | null;
  echoUpdatedAt: number | null;
  echoState: TtsEchoState | null;
  setSpeakingState: (speakingState: TtsSpeakingState) => void;
  setStopKeyArmed: (stopKeyArmed: boolean) => void;
  setEchoMemory: (
    text: string,
    echoState: TtsEchoState,
    updatedAt?: number,
  ) => void;
};

export const useTtsPlaybackStore = create<State>()(
  mirroredStore("ttsPlayback", (set) => ({
    speakingState: null,
    stopKeyArmed: false,
    completedReplyIds: [],
    completedReplyText: {},
    markReplyCompleted: (executionId, text) =>
      set((state) => {
        const completedReplyIds = [
          ...state.completedReplyIds.filter((id) => id !== executionId),
          executionId,
        ].slice(-32);
        const texts = {
          ...state.completedReplyText,
          ...(text ? { [executionId]: text } : {}),
        };
        return {
          completedReplyIds,
          completedReplyText: Object.fromEntries(
            completedReplyIds
              .filter((id) => texts[id])
              .map((id) => [id, texts[id]!]),
          ),
        };
      }),
    echoText: null,
    echoStartedAt: null,
    echoUpdatedAt: null,
    echoState: null,
    setSpeakingState: (speakingState) => set({ speakingState }),
    setStopKeyArmed: (stopKeyArmed) => set({ stopKeyArmed }),
    setEchoMemory: (text, echoState, updatedAt = Date.now()) =>
      set((state) => ({
        echoText: text,
        echoStartedAt:
          echoState === "started"
            ? updatedAt
            : state.echoText === text
              ? state.echoStartedAt
              : null,
        echoUpdatedAt: updatedAt,
        echoState,
      })),
  })),
);

export function syncTtsPlaybackState(speakingState: TtsSpeakingState) {
  useTtsPlaybackStore.getState().setSpeakingState(speakingState);
}

export function syncTtsEchoMemory(text: string, echoState: TtsEchoState) {
  useTtsPlaybackStore.getState().setEchoMemory(text, echoState);
}
